if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      constructor() {
        super();
        this.quantityInput = this.querySelector('.quantity__input');
        this.quantityForm = undefined;
        this.onVariantChangeUnsubscriber = undefined;
        this.cartUpdateUnsubscriber = undefined;
        this.abortController = undefined;
        this.pendingRequestUrl = null;
        this.preProcessHtmlCallbacks = [];
        this.postProcessHtmlCallbacks = [];
        this.lightbox = null;
        this.gallery = [];
        this.currentIndex = 0;
        this.boundKeydownHandler = this.handleKeydown.bind(this);
        this.boundClickHandler = this.handleLightboxClick.bind(this);
        this.boundZoomHandler = this.handleZoom.bind(this);
        this.boundZoomEndHandler = this.handleZoomEnd.bind(this);
      }

      connectedCallback() {
        this.initializeProductSwapUtility();
        const mediaGallery = this.querySelector('media-gallery');
        const productJsonScript = this.querySelector('script[type="application/json"][data-product-json]');

        if (mediaGallery && productJsonScript) {
          const runInitialFilter = () => {
            try {
              const productData = JSON.parse(productJsonScript.textContent);
              const currentVariantId = new URL(window.location).searchParams.get('variant');
              let initialColor = null;
              // OKB: read option name from data attribute so it's configurable per-product without touching JS
              const colorOptionName = (this.dataset.colorOptionName || 'colour').toLowerCase();
              const colorOptionIndex = productData.options.findIndex(
                optionName => optionName.toLowerCase() === colorOptionName
              );
              if (colorOptionIndex > -1) {
                const currentVariant = currentVariantId ? productData.variants.find(v => v.id == currentVariantId) : productData.variants[0];
                if (currentVariant) {
                  initialColor = currentVariant.options[colorOptionIndex];
                }

                // OKB DEV VALIDATION: warn if any gallery image's data-color doesn't match a known variant colour.
                // This catches alt-text typos early — check the browser console if filtering looks wrong.
                const knownColors = new Set(
                  productData.variants.map(v => v.options[colorOptionIndex].toLowerCase())
                );
                mediaGallery.querySelectorAll('[data-color]').forEach(item => {
                  const c = (item.dataset.color || '').trim().toLowerCase();
                  if (c !== '' && !knownColors.has(c)) {
                    console.warn(`[OKB Gallery] data-color="${item.dataset.color}" doesn't match any known colour variant. Check this image's alt text in the Shopify admin.`);
                  }
                });
              }
              if (initialColor) {
                mediaGallery.addEventListener('filter:complete', () => {
                  mediaGallery.classList.remove('gallery-initializing');
                }, { once: true });
                mediaGallery.filterByColor(initialColor);
              } else {
                mediaGallery.classList.remove('gallery-initializing');
              }
            } catch (e) {
              console.error("Product Info Error:", e);
              mediaGallery.classList.remove('gallery-initializing');
            }
          };

          const waitForGallery = () => {
            if (typeof mediaGallery.filterByColor === 'function') {
              runInitialFilter();
              this.setupLightbox();
              this.initThumbnailNextArrowLogic(); // Initialize our arrow logic.
            } else {
              requestAnimationFrame(waitForGallery);
            }
          };
          requestAnimationFrame(waitForGallery);
        } else {
          if (mediaGallery) mediaGallery.classList.remove('gallery-initializing');
        }

        if (typeof PUB_SUB_EVENTS !== "undefined" && typeof subscribe === "function") {
          this.onVariantChangeUnsubscriber = subscribe(
            PUB_SUB_EVENTS.optionValueSelectionChange,
            this.handleOptionValueChange.bind(this)
          );
        }
        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent("product-info:loaded", { bubbles: true }));
      }

      setupLightbox() {
        this.lightbox = document.getElementById('product-image-lightbox');
        if (!this.lightbox) return;
        this.cleanupLightboxTriggers();
        const mediaGallery = this.querySelector('media-gallery');
        if (!mediaGallery) return;
        const lightboxTriggers = mediaGallery.querySelectorAll('li:not([hidden]) a.product__modal-opener');
        this.gallery = [];
        lightboxTriggers.forEach((trigger) => {
          if (trigger.dataset.mediaType && trigger.dataset.mediaType !== 'image') return;
          this.gallery.push({
            src: trigger.href,
            alt: trigger.querySelector('img')?.alt || 'Product image',
          });
          trigger.addEventListener('click', this.handleTriggerClick.bind(this));
        });
      }

      handleTriggerClick(event) {
        event.preventDefault();
        const galleryIndex = this.gallery.findIndex(item => item.src === event.currentTarget.href);
        if (galleryIndex !== -1) this.openLightbox(galleryIndex);
      }

      openLightbox(index) {
        if (!this.lightbox) return;
        this.lightbox.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';
        this.loadImage(index);
        this.lightbox.addEventListener('click', this.boundClickHandler);
        document.addEventListener('keydown', this.boundKeydownHandler);
        const imageWrapper = this.lightbox.querySelector('.okb-modal__image-wrapper');
        imageWrapper.addEventListener('mousemove', this.boundZoomHandler);
        imageWrapper.addEventListener('mouseleave', this.boundZoomEndHandler);
      }

      handleLightboxClick(event) {
        const target = event.target.closest('[data-close-lightbox], [data-arrow-prev], [data-arrow-next]');
        if (!target) return;
        if (target.matches('[data-close-lightbox]')) this.closeLightbox();
        else if (target.matches('[data-arrow-prev]')) this.showPrevImage();
        else if (target.matches('[data-arrow-next]')) this.showNextImage();
      }

      loadImage(index) {
        if (index < 0 || index >= this.gallery.length) return;
        this.currentIndex = index;
        const { src, alt } = this.gallery[index];
        const imageWrapper = this.lightbox.querySelector('.okb-modal__image-wrapper');
        const imageEl = this.lightbox.querySelector('.okb-modal__image');
        const titleEl = this.lightbox.querySelector('.okb-modal__title');
        const counterEl = this.lightbox.querySelector('.okb-modal__counter');
        const prevBtn = this.lightbox.querySelector('[data-arrow-prev]');
        const nextBtn = this.lightbox.querySelector('[data-arrow-next]');
        imageWrapper.classList.add('is-loading');
        imageEl.style.opacity = '0';
        imageEl.style.transform = 'scale(1)';
        imageEl.style.cursor = 'zoom-in';
        titleEl.textContent = alt;
        counterEl.textContent = `${index + 1} of ${this.gallery.length}`;
        prevBtn.classList.toggle('is-hidden', index === 0);
        nextBtn.classList.toggle('is-hidden', index === this.gallery.length - 1);
        imageEl.onload = () => {
          imageWrapper.classList.remove('is-loading');
          imageEl.style.opacity = '1';
        };
        imageEl.src = src;
      }

      closeLightbox() {
        if (!this.lightbox) return;
        this.lightbox.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
        this.lightbox.removeEventListener('click', this.boundClickHandler);
        document.removeEventListener('keydown', this.boundKeydownHandler);
        const imageWrapper = this.lightbox.querySelector('.okb-modal__image-wrapper');
        imageWrapper.removeEventListener('mousemove', this.boundZoomHandler);
        imageWrapper.removeEventListener('mouseleave', this.boundZoomEndHandler);
      }

      showPrevImage() { this.loadImage(this.currentIndex - 1); }
      showNextImage() { this.loadImage(this.currentIndex + 1); }

      handleKeydown(event) {
        if (this.lightbox.getAttribute('aria-hidden') === 'true') return;
        if (event.key === 'Escape') this.closeLightbox();
        if (event.key === 'ArrowLeft') this.showPrevImage();
        if (event.key === 'ArrowRight') this.showNextImage();
      }

      handleZoom(event) {
        const wrapper = event.currentTarget;
        const image = wrapper.querySelector('img');
        const rect = wrapper.getBoundingClientRect();
        const x = (event.clientX - rect.left) / rect.width * 100;
        const y = (event.clientY - rect.top) / rect.height * 100;
        image.style.transformOrigin = `${x}% ${y}%`;
        image.style.transform = 'scale(2.2)';
      }

      handleZoomEnd(event) {
        const image = event.currentTarget.querySelector('img');
        image.style.transform = 'scale(1)';
      }

      updateThumbnailButtonState() {
      const thumbnailSlider = this.querySelector('slider-component[id^="GalleryThumbnails"]');
      if (!thumbnailSlider) return;

      const nextButton = thumbnailSlider.querySelector('.slider-button--next');
      const listElement = thumbnailSlider.querySelector('ul');

      if (!nextButton || !listElement) return;

      // Check if the list's total width is greater than its visible area.
      // A 1px tolerance is added to prevent issues with sub-pixel rendering.
      const isScrollable = listElement.scrollWidth > listElement.clientWidth + 1;

      // Set the button's disabled state based on whether the content is scrollable.
      nextButton.disabled = !isScrollable;

      // The theme uses a class for styling, so we'll toggle it here for consistency.
      nextButton.classList.toggle('slider-button--disabled', !isScrollable);
    }

      initThumbnailNextArrowLogic() {
      // Run the check once on initial page load.
      this.updateThumbnailButtonState();

      const mediaGallery = this.querySelector('media-gallery');
      if (mediaGallery) {
        // When the variant changes, media is filtered. We listen for the 'filter:complete'
        // event from media-gallery.js to re-check the arrow state.
        mediaGallery.addEventListener('filter:complete', () => {
          // A minimal delay ensures the DOM has fully updated before we check dimensions.
          setTimeout(() => this.updateThumbnailButtonState(), 50);
        });
      }
    }

      cleanupLightboxTriggers() {
        const mediaGallery = this.querySelector('media-gallery');
        if (!mediaGallery) return;
        const triggers = mediaGallery.querySelectorAll('a.product__modal-opener');
        triggers.forEach(trigger => trigger.replaceWith(trigger.cloneNode(true)));
      }

      disconnectedCallback() {
        console.log("ProductInfo: disconnectedCallback running.");
        this.onVariantChangeUnsubscriber?.();
        this.cartUpdateUnsubscriber?.();
        document.removeEventListener('keydown', this.boundKeydownHandler);
      }

      // --- THEME'S ORIGINAL METHODS (UNTOUCHED) ---
      
      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        // console.log("ProductInfo: initQuantityHandlers running."); // Debug log
        if (!this.quantityInput) {
          // console.log("ProductInfo initQuantityHandlers: No quantityInput, exiting.");
          return;
        }

        this.quantityForm = this.querySelector(".product-form__quantity");
        if (!this.quantityForm) {
          console.warn(
            "ProductInfo initQuantityHandlers: .product-form__quantity not found."
          );
          return;
        }

        this.setQuantityBoundries();
        if (
          !this.dataset.originalSection &&
          typeof PUB_SUB_EVENTS !== "undefined" &&
          typeof subscribe === "function"
        ) {
          this.cartUpdateUnsubscriber = subscribe(
            PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this)
          );
          // console.log("ProductInfo: Subscribed to cartUpdate for quantity rules."); // Debug log
        }
      }
      
      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html
            .querySelectorAll(".scroll-trigger")
            .forEach((element) =>
              element.classList.add("scroll-trigger--cancel")
            )
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init?.();
          window?.ProductModel?.loadShopifyXR?.();
        });
      }

      handleOptionValueChange({
        data: { event, target, selectedOptionValues },
      }) {
        // console.log("ProductInfo: handleOptionValueChange triggered."); // Debug log
        if (!this.contains(event.target)) {
          // console.log("ProductInfo handleOptionValueChange: Event target not within this element.");
          return;
        }

        this.resetProductFormState();

        const productUrl =
          target?.dataset?.productUrl ||
          this.pendingRequestUrl ||
          this.dataset.url;
        if (!productUrl) {
          console.error(
            "ProductInfo handleOptionValueChange: Could not determine productUrl."
          );
          return;
        }
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const updateUrl = this.dataset.updateUrl === "true";
        const shouldFetchFullPage = updateUrl && shouldSwapProduct;
        // console.log(`ProductInfo handleOptionValueChange: Fetching URL: ${productUrl}, Swap: ${shouldSwapProduct}, FullPage: ${shouldFetchFullPage}`);

        if (!target?.id) {
          console.error(
            "ProductInfo handleOptionValueChange: Event target has no ID."
          );
          return;
        }

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(
            productUrl,
            selectedOptionValues,
            shouldFetchFullPage
          ),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        // console.log("ProductInfo: resetProductFormState called.");
        const productForm = this.productForm;
        if (productForm?.toggleSubmitButton) {
          productForm.toggleSubmitButton(true);
          // console.log("ProductInfo resetProductFormState: Called toggleSubmitButton(true).");
        } else {
          console.warn(
            "ProductInfo resetProductFormState: productForm or toggleSubmitButton not found."
          );
        }
        productForm?.handleErrorMessage?.();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          // console.log("ProductInfo: handleSwapProduct callback running.");
          this.productModal?.remove();

          const selector = updateFullPage
            ? "product-info[id^='MainProduct']"
            : "product-info";
          const newProductInfoNode = html.querySelector(selector);
          const variant = newProductInfoNode
            ? this.getSelectedVariant(newProductInfoNode)
            : null;
          this.updateURL(productUrl, variant?.id);

          if (
            typeof HTMLUpdateUtility === "undefined" ||
            typeof HTMLUpdateUtility.viewTransition !== "function"
          ) {
            console.error(
              "ProductInfo Error: HTMLUpdateUtility or viewTransition is not defined."
            );
            return;
          }

          if (updateFullPage) {
            // console.log("ProductInfo handleSwapProduct: Updating full page.");
            const newTitle = html.querySelector("head title");
            const oldTitle = document.querySelector("head title");
            if (newTitle && oldTitle) oldTitle.innerHTML = newTitle.innerHTML;

            const mainDestination = document.querySelector("main");
            const mainSource = html.querySelector("main");
            if (mainDestination && mainSource) {
              HTMLUpdateUtility.viewTransition(
                mainDestination,
                mainSource,
                this.preProcessHtmlCallbacks,
                this.postProcessHtmlCallbacks
              );
            } else {
              console.warn(
                "ProductInfo handleSwapProduct: Missing main source or destination for full page update."
              );
            }
          } else {
            // console.log("ProductInfo handleSwapProduct: Updating product-info section.");
            const infoSource = html.querySelector("product-info");
            if (infoSource) {
              HTMLUpdateUtility.viewTransition(
                this,
                infoSource,
                this.preProcessHtmlCallbacks,
                this.postProcessHtmlCallbacks
              );
            } else {
              console.warn(
                "ProductInfo handleSwapProduct: Missing product-info source for section update."
              );
            }
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        // console.log(`ProductInfo: renderProductInfo called for URL: ${requestUrl}`);
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => {
            if (!response.ok)
              throw new Error(`Workspace failed: ${response.status}`);
            // console.log(`ProductInfo renderProductInfo: Fetch successful for ${requestUrl}`);
            return response.text();
          })
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );
            // console.log("ProductInfo renderProductInfo: Parsed response HTML.");
            if (typeof callback === "function") {
              // console.log("ProductInfo renderProductInfo: Executing callback.");
              callback(html);
            } else {
              console.warn(
                "ProductInfo renderProductInfo: Callback provided is not a function."
              );
            }
          })
          .then(() => {
            const targetElement = document.querySelector(`#${targetId}`);
            if (targetElement) {
              targetElement.focus();
              // console.log(`ProductInfo renderProductInfo: Focused target #${targetId}`);
            } else {
              console.warn(
                `ProductInfo renderProductInfo: Target element #${targetId} not found for focus.`
              );
            }
          })
          .catch((error) => {
            if (error.name === "AbortError") {
              // console.log("Fetch aborted by user");
            } else {
              console.error("Fetch error in renderProductInfo:", error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariantElement = productInfoNode?.querySelector(
          "variant-selects [data-selected-variant]"
        );
        const selectedVariantHTML = selectedVariantElement?.innerHTML;
        try {
          return !!selectedVariantHTML ? JSON.parse(selectedVariantHTML) : null;
        } catch (e) {
          console.error(
            "Error parsing selected variant JSON:",
            e,
            selectedVariantHTML
          );
          return null;
        }
      }

      buildRequestUrlWithParams(
        url,
        optionValues,
        shouldFetchFullPage = false
      ) {
        const params = [];
        const currentSectionId = this.sectionId;
        if (!shouldFetchFullPage && currentSectionId) {
          params.push(`section_id=${currentSectionId}`);
        }
        if (optionValues?.length) {
          params.push(`option_values=${optionValues.join(",")}`);
        }
        return `${url}?${params.join("&")}`;
      }

      updateOptionValues(html) {
        // console.log("ProductInfo: updateOptionValues running.");
        const variantSelectsSource = html.querySelector("variant-selects");
        const variantSelectsDestination = this.variantSelectors;
        if (
          variantSelectsSource &&
          variantSelectsDestination &&
          typeof HTMLUpdateUtility !== "undefined" &&
          typeof HTMLUpdateUtility.viewTransition === "function"
        ) {
          // console.log("ProductInfo updateOptionValues: Found elements, performing view transition.");
          HTMLUpdateUtility.viewTransition(
            variantSelectsDestination,
            variantSelectsSource,
            this.preProcessHtmlCallbacks
          );
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          // console.log("%cProductInfo: handleUpdateProductInfo callback entered.", "color: blue; font-weight: bold;");
          const variant = this.getSelectedVariant(html);
          // console.log("ProductInfo handleUpdateProductInfo: Found variant in fetched HTML:", variant);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            // console.log(
              // "ProductInfo handleUpdateProductInfo: No valid variant found, calling setUnavailable."
            // );
            this.setUnavailable();
            // console.log(
              // "ProductInfo handleUpdateProductInfo: Callback finished (no variant)."
            // );
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);
          
          setTimeout(() => {
            this.setupLightbox();
          }, 50);
          
          setTimeout(() => {
            const mediaGallery = this.querySelector('media-gallery');
            const variantPicker = this.querySelector('variant-selects');
            if (mediaGallery && variantPicker && typeof mediaGallery.filterByColor === 'function') {
              const colorFieldset = variantPicker.querySelector('.product-form__input--swatch, .product-form__input--pill');
              if(colorFieldset) {
                const selectedColorInput = colorFieldset.querySelector('input:checked');
                if (selectedColorInput) {
                  const selectedColor = selectedColorInput.value;
                  mediaGallery.filterByColor(selectedColor);
                }
              }
            }
          }, 0);
          
          const updateSourceFromDestination = (
            id,
            shouldHide = (source) => false
          ) => {
            const currentSectionId = this.dataset.section;
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(
              `#${id}-${currentSectionId}`
            );
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle("hidden", shouldHide(source));
            }
          };
          updateSourceFromDestination("price");
          updateSourceFromDestination("Sku", ({ classList }) =>
            classList.contains("hidden")
          );
          updateSourceFromDestination(
            "Inventory",
            ({ innerText }) => innerText === ""
          );
          updateSourceFromDestination("Volume");
          updateSourceFromDestination("Price-Per-Item", ({ classList }) =>
            classList.contains("hidden")
          );

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(
            `#Quantity-Rules-${this.dataset.section}`
          )?.classList.remove("hidden");
          this.querySelector(
            `#Volume-Note-${this.dataset.section}`
          )?.classList.remove("hidden");

          const productForm = this.productForm;
          if (productForm) {
            const submitButtonId = `ProductSubmitButton-${this.sectionId}`;
            const submitButtonInFetchedHtml =
              html.getElementById(submitButtonId);
            const isVariantAvailable = variant?.available === true;
            const isButtonDisabledInHtml =
              submitButtonInFetchedHtml?.hasAttribute("disabled") ??
              !isVariantAvailable;
            const isVariantDisabled =
              !isVariantAvailable || isButtonDisabledInHtml;
            const buttonText = isVariantDisabled
              ? window.variantStrings?.soldOut || "Sold Out"
              : window.variantStrings?.addToCart || "Add To Cart";
            if (typeof productForm.toggleSubmitButton === "function") {
              productForm.toggleSubmitButton(isVariantDisabled, buttonText);
            }
          }

          if (
            typeof PUB_SUB_EVENTS !== "undefined" &&
            typeof publish === "function"
          ) {
            publish(PUB_SUB_EVENTS.variantChange, {
              data: {
                sectionId: this.sectionId,
                html,
                variant,
              },
            });
          }
          // console.log(
            // "%cProductInfo: handleUpdateProductInfo callback finished.",
            // "color: blue; font-weight: bold;"
          // );
        };
      }

      updateVariantInputs(variantId) {
        // console.log(
        // `ProductInfo: updateVariantInputs called with variantId: ${variantId}`
        // );
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          if (input) {
            input.value = variantId ?? "";
            input.dispatchEvent(new Event("change", { bubbles: true }));
          } else {
            console.warn(
              'ProductInfo updateVariantInputs: Could not find input[name="id"] in form:',
              productForm
            );
          }
        });
      }

      updateURL(url, variantId) {
        // console.log(
          // `ProductInfo: updateURL called. URL: ${url}, VariantID: ${variantId}`
        // );
        const shareButton = this.querySelector("share-button");
        if (shareButton?.updateUrl) {
          shareButton.updateUrl(
            `${window.shopUrl || ""}${url}${
              variantId ? `?variant=${variantId}` : ""
            }`
          );
        }

        if (this.dataset.updateUrl === "false") return;
        window.history.replaceState(
          {},
          "",
          `${url}${variantId ? `?variant=${variantId}` : ""}`
        );
      }

      setUnavailable() {
        // console.log("ProductInfo: setUnavailable called.");
        const productForm = this.productForm;
        if (productForm?.toggleSubmitButton) {
          productForm.toggleSubmitButton(
            true,
            window.variantStrings?.unavailable || "Unavailable"
          );
        } else {
          console.warn(
            "ProductInfo setUnavailable: productForm or toggleSubmitButton not found."
          );
        }

        const selectors = [
          "price",
          "Inventory",
          "Sku",
          "Price-Per-Item",
          "Volume-Note",
          "Volume",
          "Quantity-Rules",
        ]
          .map((id) => `#${id}-${this.sectionId}`)
          .join(", ");
        document
          .querySelectorAll(selectors)
          .forEach(({ classList }) => classList.add("hidden"));
      }

      updateMedia(html, variantFeaturedMediaId) {
        // console.log(
         // `ProductInfo: updateMedia called. Media ID: ${variantFeaturedMediaId}`
        // );
        if (!variantFeaturedMediaId) {
          // console.log(
           // "ProductInfo updateMedia: No featured media ID provided."
          // );
          return;
        }

        const mediaGallerySource = this.querySelector("media-gallery ul");
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        if (!mediaGallerySource) {
          console.warn(
            "ProductInfo updateMedia: Could not find mediaGallerySource (media-gallery ul) in current DOM."
          );
          return;
        }
        if (!mediaGalleryDestination) {
          console.warn(
            "ProductInfo updateMedia: Could not find mediaGalleryDestination (media-gallery ul) in fetched HTML."
          );
          const mediaGallery = this.querySelector(`media-gallery`);
          if (mediaGallery?.setActiveMedia) {
            mediaGallery.setActiveMedia(
              `${this.sectionId}-${variantFeaturedMediaId}`,
              true
            );
          }
          return;
        }

        const refreshSourceData = () => {
          if (
            this.hasAttribute("data-zoom-on-hover") &&
            typeof enableZoomOnHover === "function"
          ) {
            try {
              enableZoomOnHover(2);
            } catch (e) {
              console.error("Error calling enableZoomOnHover:", e);
            }
          }
          if (!mediaGallerySource) return [[], new Set(), new Map()];
          const mediaGallerySourceItems = Array.from(
            mediaGallerySource.querySelectorAll("li[data-media-id]")
          );
          const sourceSet = new Set(
            mediaGallerySourceItems.map((item) => item.dataset.mediaId)
          );
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [
              item.dataset.mediaId,
              { item, index },
            ])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        try {
          let [mediaGallerySourceItems, sourceSet, sourceMap] =
            refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll("li[data-media-id]")
          );
          const destinationSet = new Set(
            mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId)
          );
          let shouldRefresh = false;

          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (
              !sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)
            ) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          for (let i = mediaGallerySourceItems.length - 1; i >= 0; i--) {
            if (
              !destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)
            ) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          if (shouldRefresh)
            [mediaGallerySourceItems, sourceSet, sourceMap] =
              refreshSourceData();

          mediaGalleryDestinationItems.forEach(
            (destinationItem, destinationIndex) => {
              const sourceData = sourceMap.get(destinationItem.dataset.mediaId);
              if (sourceData && sourceData.index !== destinationIndex) {
                const referenceNode = mediaGallerySource.querySelector(
                  `li:nth-of-type(${destinationIndex + 1})`
                );
                mediaGallerySource.insertBefore(sourceData.item, referenceNode);
                [mediaGallerySourceItems, sourceSet, sourceMap] =
                  refreshSourceData();
              }
            }
          );
          // console.log("ProductInfo updateMedia: Gallery sync completed.");
        } catch (e) {
          console.error("Error during media gallery synchronization:", e);
        }

        const mediaGallery = this.querySelector(`media-gallery`);
        if (mediaGallery?.setActiveMedia) {
          // console.log(
            // `ProductInfo updateMedia: Setting active media to ${this.sectionId}-${variantFeaturedMediaId}`
          // );
          mediaGallery.setActiveMedia(
            `${this.sectionId}-${variantFeaturedMediaId}`,
            true
          );
        } else {
          console.warn(
            "ProductInfo updateMedia: media-gallery or setActiveMedia method not found."
          );
        }

        const productModal = this.productModal;
        const modalContent = productModal?.querySelector(
          `.product-media-modal__content`
        );
        const newModalContent = html.querySelector(
          `product-modal .product-media-modal__content`
        );
        if (modalContent && newModalContent) {
          modalContent.innerHTML = newModalContent.innerHTML;
          // console.log(
           // "ProductInfo updateMedia: Updated product modal content."
          // );
        }
      }

      setQuantityBoundries() {
        if (!this.quantityInput) {
          console.warn("setQuantityBoundries: quantityInput not found.");
          return;
        }
        try {
          const data = {
            cartQuantity: this.quantityInput.dataset.cartQuantity
              ? parseInt(this.quantityInput.dataset.cartQuantity, 10)
              : 0,
            min: this.quantityInput.dataset.min
              ? parseInt(this.quantityInput.dataset.min, 10)
              : 1,
            max: this.quantityInput.dataset.max
              ? parseInt(this.quantityInput.dataset.max, 10)
              : null,
            step: this.quantityInput.step
              ? parseInt(this.quantityInput.step, 10)
              : 1,
          };

          let min = data.min >= 1 ? data.min : 1;
          const max =
            data.max === null ? data.max : data.max - data.cartQuantity;

          if (max !== null && min > max) {
            min = max > 0 ? max : 1;
          }

          this.quantityInput.min = min;

          if (max !== null && max >= min) {
            this.quantityInput.max = max;
          } else {
            this.quantityInput.removeAttribute("max");
          }
          let currentValue = parseInt(this.quantityInput.value, 10) || min;
          currentValue = Math.max(min, currentValue);
          if (max !== null) currentValue = Math.min(max, currentValue);
          this.quantityInput.value = min;

          if (
            typeof publish === "function" &&
            typeof PUB_SUB_EVENTS !== "undefined"
          ) {
            publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
          }
        } catch (e) {
          console.error("Error in setQuantityBoundries:", e);
        }
      }

      fetchQuantityRules() {
        const productForm = this.productForm;
        const currentVariantId = productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        const spinner = this.querySelector(
          ".quantity__rules-cart .loading__spinner"
        );
        if (spinner) spinner.classList.remove("hidden");

        fetch(
          `${this.dataset.url}?variant=${currentVariantId}&section_id=${this.sectionId}`
        )
          .then((response) => {
            if (!response.ok)
              throw new Error(`Workspace failed: ${response.status}`);
            return response.text();
          })
          .then((responseText) => {
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );
            this.updateQuantityRules(this.sectionId, html);
          })
          .catch((e) => console.error("Error fetching quantity rules:", e))
          .finally(() => {
            if (spinner) spinner.classList.add("hidden");
          });
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput || !this.quantityForm) {
          console.warn(
            "UpdateQuantityRules: Missing quantityInput or quantityForm."
          );
          return;
        }

        const quantityFormUpdated = html.getElementById(
          `Quantity-Form-${sectionId}`
        );
        if (!quantityFormUpdated) {
          console.warn(
            `UpdateQuantityRules: Element Quantity-Form-${sectionId} not found in fetched HTML.`
          );
          return;
        }
        
        const selectors = [
          ".quantity__input",
          ".quantity__rules",
          ".quantity__label",
        ];
        let needsBoundaryUpdate = false;
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) {
            console.warn(
              `UpdateQuantityRules: Missing current or updated element for selector: ${selector}`
            );
            continue;
          }
          if (selector === ".quantity__input") {
            const attributes = [
              "data-cart-quantity",
              "data-min",
              "data-max",
              "step",
            ];
            for (let attribute of attributes) {
              const currentAttrValue = current.getAttribute(attribute);
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                if (currentAttrValue !== valueUpdated) {
                  current.setAttribute(attribute, valueUpdated);
                  needsBoundaryUpdate = true;
                }
              } else {
                if (currentAttrValue !== null) {
                  current.removeAttribute(attribute);
                  needsBoundaryUpdate = true;
                }
              }
            }
            if (current.value !== updated.value) {
              current.value = updated.value;
              needsBoundaryUpdate = true;
            }
          } else {
            if (current.innerHTML !== updated.innerHTML) {
              current.innerHTML = updated.innerHTML;
            }
          }
        }
        if (needsBoundaryUpdate) {
          // console.log(
           //  "UpdateQuantityRules: Re-applying quantity boundaries after updates."
          // );
          this.setQuantityBoundries();
        }
      }

      get productForm() {
        return this.querySelector("product-form");
      }

      get productModal() {
        const currentSectionId = this.sectionId;
        if (!currentSectionId) return null;
        const id = `#ProductModal-${currentSectionId}`;
        return document.querySelector(id);
      }

      get pickupAvailability() {
        return this.querySelector("pickup-availability");
      }

      get variantSelectors() {
        return this.querySelector("variant-selects");
      }

      get relatedProducts() {
        try {
          const currentSectionId = this.sectionId;
          if (!currentSectionId || typeof SectionId === "undefined")
            return null;
          const relatedProductsSectionId = SectionId.getIdForSection(
            SectionId.parseId(currentSectionId),
            "related-products"
          );
          return document.querySelector(
            `product-recommendations[data-section-id^="${relatedProductsSectionId}"]`
          );
        } catch (e) {
          console.error("Error getting relatedProducts section ID:", e);
          return null;
        }
      }

      get quickOrderList() {
        try {
          const currentSectionId = this.sectionId;
          if (!currentSectionId || typeof SectionId === "undefined")
            return null;
          const quickOrderListSectionId = SectionId.getIdForSection(
            SectionId.parseId(currentSectionId),
            "quick_order_list"
          );
          return document.querySelector(
            `quick-order-list[data-id^="${quickOrderListSectionId}"]`
          );
        } catch (e) {
          console.error("Error getting quickOrderList section ID:", e);
          return null;
        }
      }

       get sectionId() {
        return this.dataset.originalSection ?? this.dataset.section ?? null;
      }
    }
  );
}
